import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import SelectContentFolder from "../../../Gallery/SelectContentFolder";
import SelectContentLanguage from "../../../Form/SelectContentLanguage";
import Switch from "../../../Form/Switch";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  ADS_CAMPAIGN_LANGUAGE_SELECT_PLACEHOLDER,
  AUTOMATIONS_SOURCE_ALL_DESCRIPTION,
  AUTOMATIONS_SOURCE_ALL_TITLE,
  AUTOMATIONS_SOURCE_FOLDER_DESCRIPTION,
  AUTOMATIONS_SOURCE_FOLDER_TITLE,
  AUTOMATIONS_SOURCE_INCLUDE_SUBFOLDERS,
  AUTOMATIONS_SOURCE_INCLUDE_SUBFOLDERS_HELP,
  AUTOMATIONS_STEP_LANGUAGE_HELP,
  AUTOMATIONS_STEP_LANGUAGE_TITLE,
  AUTOMATIONS_STEP_SOURCE_HELP,
  AUTOMATIONS_STEP_SOURCE_TITLE,
  FIELD_REQUIRED,
  GALLERY_FOLDERS_SELECT,
} from "../../../../i18n/keysTranslations";
import { CONTENT_LANGUAGE_PRESET } from "../../../../utils/catalogs/contentLanguage";
import AutomationStepSection from "../AutomationStepSection";
import {
  AUTOMATION_SOURCE_ALL,
  AUTOMATION_SOURCE_FOLDER,
} from "../automationModalConstants";
import { subtleBorderSx } from "../automationModalStyles";

const StepSource = ({
  formState,
  onChange,
  businessName,
  control,
  errors,
  folders = [],
  isLoadingFolders = false,
  isCreatingFolder = false,
  onOpenCreateFolder,
}) => {
  const { t } = useTranslationApp();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <AutomationStepSection
      title={t(AUTOMATIONS_STEP_SOURCE_TITLE)}
      description={t(AUTOMATIONS_STEP_SOURCE_HELP, {
        NAME_BUSINESS: businessName,
      })}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <RadioGroup
          value={formState.sourceType}
          onChange={(e) => onChange({ sourceType: e.target.value })}
          sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
        >
          <ChoiceRow
            value={AUTOMATION_SOURCE_ALL}
            title={t(AUTOMATIONS_SOURCE_ALL_TITLE)}
            description={t(AUTOMATIONS_SOURCE_ALL_DESCRIPTION)}
          />
          <ChoiceRow
            value={AUTOMATION_SOURCE_FOLDER}
            title={t(AUTOMATIONS_SOURCE_FOLDER_TITLE)}
            description={t(AUTOMATIONS_SOURCE_FOLDER_DESCRIPTION)}
          >
            {formState.sourceType === AUTOMATION_SOURCE_FOLDER && (
              <Box sx={{ mt: 2, width: "100%" }}>
                <SelectContentFolder
                  name="folderID"
                  control={control}
                  errors={errors}
                  folders={folders}
                  label={t(GALLERY_FOLDERS_SELECT)}
                  disabled={isLoadingFolders || isCreatingFolder}
                  onOpenCreateFolder={onOpenCreateFolder}
                  showDestinationPath={false}
                  fullWidth
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <Switch
                    hasLabel={false}
                    size="small"
                    isActive={formState.includeSubfolders}
                    onChangeIsActive={(checked) =>
                      onChange({ includeSubfolders: checked })
                    }
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {t(AUTOMATIONS_SOURCE_INCLUDE_SUBFOLDERS)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ overflowWrap: "anywhere" }}
                    >
                      {t(AUTOMATIONS_SOURCE_INCLUDE_SUBFOLDERS_HELP)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </ChoiceRow>
        </RadioGroup>
      </Box>
    </AutomationStepSection>

      <AutomationStepSection
        title={t(AUTOMATIONS_STEP_LANGUAGE_TITLE)}
        description={t(AUTOMATIONS_STEP_LANGUAGE_HELP)}
      >
        <SelectContentLanguage
          name="language"
          preset={CONTENT_LANGUAGE_PRESET.ALL}
          disableClearable
          fullWidth
          variant="filled"
          placeholder={t(ADS_CAMPAIGN_LANGUAGE_SELECT_PLACEHOLDER)}
          control={control}
          errors={errors}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
        />
      </AutomationStepSection>
    </Box>
  );
};

const ChoiceRow = ({ value, title, description, children }) => (
  <Box
    sx={{
      ...subtleBorderSx,
      p: 2,
    }}
  >
    <FormControlLabel
      value={value}
      control={<Radio size="small" />}
      label={
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 0.3,
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ lineHeight: 1.35, overflowWrap: "anywhere" }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: 1.35, display: "block", overflowWrap: "anywhere" }}
          >
            {description}
          </Typography>
          {children}
        </Box>
      }
      sx={{
        alignItems: "flex-start",
        m: 0,
        width: "100%",
        "& .MuiFormControlLabel-label": {
          width: "100%",
          minWidth: 0,
        },
      }}
    />
  </Box>
);

export default StepSource;
